import React, { ComponentPropsWithRef, useState } from 'react'
import { Assignation } from '../../context/types'
import { StatementCard } from '../../components/StatementCard'
import { useCommonCtx } from '../../context/useCommonCtx'
import { ScaleItem } from './types'
import { TKeys } from '../../i18n/types'
import { AnswserScoreType, NO_SCORE } from '../../services/types'
import { ScaleSlider } from './ScaleSlider'
import { CommentModal } from './CommentModal'
import { Button } from '@mui/material'
import { Icons } from '../../assets/icons'
import { useTranslation } from '../../i18n/useTranslation'

interface Props extends ComponentPropsWithRef<'div'> {
    stm: Assignation
    fsmState: string
}

const MAX_VALUE = 7 as AnswserScoreType['Score'] // 10
const AVG_VALUE = 4 as AnswserScoreType['Score'] // 5
const MIN_VALUE = 1 as AnswserScoreType['Score'] // 0
const SCORE_PREFIX = 'lvlx' //'lvlSC10x'
const MARKS_VALUES = [MIN_VALUE, 2, 3, AVG_VALUE, 4, 5, 6, MAX_VALUE] //[0, 2, 8, 10];
const options: ScaleItem[] = Array(MAX_VALUE)
    .fill(0)
    .map((_, i) => ({
        key: `${SCORE_PREFIX}${i + 1}` as TKeys,
        value: (i + 1) as AnswserScoreType['Score'],
    }))

const avgOptions = options.find(
    (o) => o.value === AVG_VALUE
) as (typeof options)[number]

const NO_SCORE_OPTION = {
    key: 'lvl0' as TKeys,
    value: NO_SCORE as AnswserScoreType['Score'],
}

console.debug({
    options,
    avgOptions,
    NO_SCORE_OPTION,
})

export const ScalePicker = (p: Props) => {
    const { stm, fsmState } = p
    const commons = useCommonCtx()
    const {
        state: { params },
        setScannedItems,
        scannedItems,
    } = commons
    const item = scannedItems.find(
        (s) => s.RefQuestion === stm.valueKey
    ) as AnswserScoreType
    const t = useTranslation(commons)

    const [showCommentModal, setShowCommentModal] = useState(false)

    return (
        <div className="flex flex-1 flex-col justify-center items-center">
            <CommentModal
                key={stm.valueKey} 
                open={showCommentModal}
                initialComment={item.Comment}
                onClose={() => setShowCommentModal(false)}
                onConfirm={(Comment) => {
                    setScannedItems((prev) =>
                        prev.map((s) =>
                            s.RefQuestion === item.RefQuestion
                                ? { ...s, Comment }
                                : s
                        )
                    )
                    setShowCommentModal(false)
                }}
            />
            <StatementCard
                big
                showId={params.assessmentParams['debug_mode'] === 1}
                emoji={!!params.assessmentParams['values_with_icons']}
                onlyLongPress
                style={{
                    width: '100%',
                    flex: 1,
                }}
                {...stm}
            />
            <ScaleSlider
                className="flex-1"
                options={options}
                noScoreOption={NO_SCORE_OPTION}
                scaleValue={item.Score}
                minValue={MIN_VALUE}
                maxValue={MAX_VALUE}
                avgValue={AVG_VALUE}
                marksValues={MARKS_VALUES}
                onPickScale={(v) =>
                    setScannedItems((prev) =>
                        prev.map((s) =>
                            s.RefQuestion === item.RefQuestion
                                ? { ...s, Score: v }
                                : s
                        )
                    )
                }
            >
                <Button
                    variant="contained"
                    onClick={() => setShowCommentModal(true)}
                    className="flex flex-row items-center gap-5"
                >
                    {item.Comment?.length ? (
                        <Icons.Comment
                            sx={{ fontSize: '2rem' }}
                            titleAccess={t('comment_title')}
                        />
                    ) : (
                        <Icons.AddComment
                            sx={{ fontSize: '2rem' }}
                            titleAccess={t('comment_title')}
                        />
                    )}
                    {t('comment_title')}
                </Button>
            </ScaleSlider>
        </div>
    )
}
